import React from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Layout } from './components/Layout';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import Applicants from './pages/Applicants';
import CreateCase from './pages/CreateCase';
import CaseDetail from './pages/CaseDetail';
import TestDashboard from './pages/TestDashboard';
import CommunicationCentre from './pages/CommunicationCentre';
import SyllabusLibrary from './pages/SyllabusLibrary';
import SettingsPage from './pages/SettingsPage';
import AuditLog from './pages/AuditLog';
import DocumentUpload from './pages/DocumentUpload';
import AdmitCard from './pages/AdmitCard';
import AdmitCardPrint from './pages/AdmitCardPrint';
import WelcomeLetter from './pages/WelcomeLetter';
import PendingApprovals from './pages/PendingApprovals';
import { GraduationCap } from 'lucide-react';

function Spinner() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[#F5F7FB]">
      <span className="flex h-12 w-12 animate-pulse items-center justify-center rounded-xl bg-navy text-white"><GraduationCap size={26} /></span>
    </div>
  );
}

function Protected({ roles, children }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

const STAFF = ['office', 'rector'];
const staffPage = (el) => <Protected roles={STAFF}><Layout>{el}</Layout></Protected>;

function LoginRoute() {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (user) return <Navigate to="/dashboard" replace />;
  return <Login />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<LoginRoute />} />
      <Route path="/callback" element={<AuthCallback />} />
      <Route path="/upload/:token" element={<DocumentUpload />} />
      <Route path="/documents/:token" element={<DocumentUpload />} />
      <Route path="/admit/:token" element={<AdmitCard />} />
      <Route path="/welcome/:token" element={<WelcomeLetter />} />
      <Route path="/tests/print" element={staffPage(<AdmitCardPrint />)} />
      <Route path="/dashboard" element={staffPage(<Dashboard />)} />
      <Route path="/applicants" element={staffPage(<Applicants />)} />
      <Route path="/applicants/new" element={staffPage(<CreateCase />)} />
      <Route path="/cases/:id" element={staffPage(<CaseDetail />)} />
      <Route path="/tests" element={staffPage(<TestDashboard />)} />
      <Route path="/communications" element={staffPage(<CommunicationCentre />)} />
      <Route path="/syllabus" element={staffPage(<SyllabusLibrary />)} />
      <Route path="/audit" element={<Protected roles={['rector']}><Layout><AuditLog /></Layout></Protected>} />
      <Route path="/approvals" element={<Protected roles={['rector']}><Layout><PendingApprovals /></Layout></Protected>} />
      <Route path="/settings" element={<Protected roles={['rector']}><Layout><SettingsPage /></Layout></Protected>} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <AppRouter />
      </BrowserRouter>
    </AuthProvider>
  );
}
